import { Providers } from "@amzn/ads-blink-client";
import type { SortOptions } from "@amzn/ads-blink-client";
import type { Draft } from "immer";
import { produce } from "immer";
import { isEqual } from "lodash";

import type {
    AllFilterOptionId,
    AllFilterOptionModelType,
    FilterValues,
    UnifiedFilterState,
} from "../types/filters/common";
import type {
    AllUnifiedFilterKeys,
    UnifiedTypeGroups,
} from "../types/filters/unified";
import { getProcessedInitialUnifiedFilterState } from "../utils/filters.utils";

import { zustandStore } from "./zustand.helpers";

// TODO: Add a function to get all selectedFilter
export interface UnifiedFilterActions {
    setGroup: (group: UnifiedTypeGroups) => void;
    /**
     * Toggles the "selected" state of a filter option, marking it as either selected or unselected
     * @param filterKey The key for the option we want to toggle
     * @param id The id for the option we want to toggle
     */
    toggleSelected: (
        filterKey: AllUnifiedFilterKeys,
        id: AllFilterOptionModelType["id"],
    ) => void;

    /**
     * Selects all of the filter options for the given key
     * @param filterKey The key for the options we want to select
     */
    selectAll: (filterKey: AllUnifiedFilterKeys) => void;

    /**
     * Deselects all of the filter options for the given key
     * @param filterKey The key for the options we want to deselect
     * @returns True if the filter was changed, false otherwise
     */
    deselectAll: (filterKey: AllUnifiedFilterKeys) => boolean;

    /**
     * Sets the selected values for the given filter key, and deselects the other values
     * @param filterKey The key of the filter whose values we want to set
     * @param ids The ids we want to have selected
     */
    setSelectedValues: (
        filterKey: AllUnifiedFilterKeys,
        ids: AllFilterOptionId[],
    ) => void;

    /**
     * Itterates through all of the available scopes and disables any of the scopes whose id doesn't appear in the provided list
     * @param scopes The list of scope ids that we want to enable
     */
    setEnabledTypes: (scopes: string[]) => void;

    /**
     * Sets the stored filter values for the given filter key
     * @param filterKey The key for the filter we want to set
     * @param values The values for the filter we want to set
     */
    setFilters: <K extends AllUnifiedFilterKeys>(
        filterKey: K,
        values: FilterValues<K>,
    ) => void;

    /**
     * Sets the search query for the unified filters
     * @param query The search query we want to set
     */
    setSearchQuery: (query: string) => void;

    /**
     * Marks the content state as dirty, which will trigger a re-render of the content
     */
    setIsContentDirty: (isDirty: boolean) => void;

    /**
     * Marks the metadata state as dirty, which will trigger a re-render of the filters
     */
    setIsMetadataDirty: (isDirty: boolean) => void;

    /**
     * Triggers AI search
     */
    setIsAiSearchTriggered: (isTriggered: boolean) => void;

    /**
     * Returns true if the contents are returned from the filters or searchQuery currently in the store
     */
    isLastSearchParametersDifferent: () => boolean;

    /**
     * Sets the last search filters and query field so we know if the current filters are aligned with the contents shown
     */
    setLastSearchParameters: () => void;

    /**
     * Sets the last search total results so we can conditionally display tooltips and messages
     */
    setLastSearchResultTotalCount: (totalCount: number) => void;

    /**
     * Sets the sort attribute on the store
     */
    setSortAttribute: (sortAttribute: SortOptions) => void;

    /**
     * Sets the current quick filter ID
     */
    setCurrentQuickFilterId: (id: string | undefined) => void;
}

export const { store: unifiedFilterStore } = zustandStore<
    UnifiedFilterState,
    UnifiedFilterActions
>("UnifiedFilters", getProcessedInitialUnifiedFilterState(), (set, get) => ({
    setGroup: (group: UnifiedTypeGroups) => {
        set((state) => {
            return produce(state, (draft) => {
                draft.group = group;
                return draft;
            });
        });
    },
    setEnabledTypes: (types: string[]) => {
        set((state) => {
            return produce(state, (draft) => {
                draft.filters.UNIFIED_TYPE.forEach((type) => {
                    if (type.id === "Industry Insights - Marketing") {
                        // Check if user has access to MARKETING_NARRATIVE provider
                        const hasMarketingProviderAccess =
                            draft.filters.UNIFIED_TYPE.filter((t) =>
                                t.scopes?.includes(
                                    Providers.MARKETING_NARRATIVE,
                                ),
                            ).some((t) => types.includes(t.id));
                        const wasDisabled = type.isDisabled;
                        const wasSelected = type.isSelected;
                        type.isDisabled = !hasMarketingProviderAccess;
                        if (
                            wasDisabled &&
                            wasSelected &&
                            hasMarketingProviderAccess
                        ) {
                            type.isSelected = true;
                        } else if (!hasMarketingProviderAccess) {
                            type.isSelected = false;
                        }
                    } else {
                        // Standard logic for all other types
                        type.isDisabled = !types.includes(type.id);
                    }
                });
                return draft;
            });
        });
    },

    setFilters<K extends AllUnifiedFilterKeys>(
        filterKey: K,
        values: FilterValues<K>,
    ) {
        set((state) => {
            return produce(state, (draft) => {
                (draft.filters[filterKey] as Draft<FilterValues<K>>) =
                    values as Draft<FilterValues<K>>;
                return draft;
            });
        });
    },

    setSearchQuery: (query: string) => {
        set((state) => {
            return produce(state, (draft) => {
                draft.searchQuery = query;
                return draft;
            });
        });
    },

    setIsContentDirty: (isdirty: boolean) => {
        set((state) => {
            return produce(state, (draft) => {
                draft.isContentDirty = isdirty;
                return draft;
            });
        });
    },

    setIsMetadataDirty: (isdirty: boolean) => {
        set((state) => {
            return produce(state, (draft) => {
                draft.isMetadataDirty = isdirty;
                return draft;
            });
        });
    },

    setIsAiSearchTriggered: (isTriggered: boolean) => {
        set((state) => {
            return produce(state, (draft) => {
                draft.isAiSearchTriggered = isTriggered;
                return draft;
            });
        });
    },

    toggleSelected: (
        filterKey: AllUnifiedFilterKeys,
        id: AllFilterOptionModelType["id"],
    ) => {
        set((state) => {
            return produce(state, (draft) => {
                const index = draft.filters[filterKey].findIndex((item) =>
                    isEqual(item.id, id),
                );
                if (index !== -1) {
                    draft.filters[filterKey][index].isSelected =
                        !draft.filters[filterKey][index].isSelected;
                }
                return draft;
            });
        });
    },

    selectAll: (filterKey: AllUnifiedFilterKeys) => {
        set((state) => {
            return produce(state, (draft) => {
                draft.filters[filterKey].forEach(
                    (item) =>
                        (item.isSelected =
                            "isDisabled" in item ? !item.isDisabled : true),
                );
                return draft;
            });
        });
    },

    deselectAll: (filterKey: AllUnifiedFilterKeys) => {
        let filterChanged = false;
        set((state) => {
            return produce(state, (draft) => {
                draft.filters[filterKey].forEach((item) => {
                    filterChanged ||= item.isSelected;
                    item.isSelected = false;
                });
                return draft;
            });
        });
        return filterChanged;
    },

    setSelectedValues: (
        filterKey: AllUnifiedFilterKeys,
        ids: AllFilterOptionId[],
    ) => {
        set((state) => {
            return produce(state, (draft) => {
                draft.filters[filterKey].forEach((item) => {
                    item.isSelected = ids.some((id) => isEqual(id, item.id));
                });
                return draft;
            });
        });
    },

    isLastSearchParametersDifferent: () => {
        const state = get();
        const currentSelectedFilterIds = Object.values(state.filters).map(
            (filter: { isSelected: boolean; id: any }[]) =>
                filter
                    .filter((val) => val.isSelected)
                    .map((val) => val.id)
                    .filter((f) => f.length > 0),
        );

        const isSearchQuerySame =
            state.lastSearchParameters.searchQuery === state.searchQuery;
        const isFilterSame = isEqual(
            state.lastSearchParameters.selectedFilterIds,
            currentSelectedFilterIds,
        );

        return !(isSearchQuerySame && isFilterSame);
    },

    setLastSearchParameters: () => {
        set((state) => {
            const searchQuery = state.searchQuery;
            const selectedFilterIds = Object.values(state.filters).map(
                (filter: { isSelected: boolean; id: any }[]) =>
                    filter
                        .filter((val) => val.isSelected)
                        .map((val) => val.id)
                        .filter((f) => f.length > 0),
            );
            return produce(state, (draft) => {
                draft.lastSearchParameters = {
                    searchQuery,
                    selectedFilterIds,
                };
                return draft;
            });
        });
    },

    setLastSearchResultTotalCount: (totalCount: number) => {
        set((state) => {
            return produce(state, (draft) => {
                draft.lastSearchResultTotalCount = totalCount;
                return draft;
            });
        });
    },

    setSortAttribute: (sortAttribute: SortOptions) => {
        set((state) => {
            return produce(state, (draft) => {
                draft.sortAttribute = sortAttribute;
                return draft;
            });
        });
    },

    setCurrentQuickFilterId: (id: string | undefined) => {
        set((state) => {
            return produce(state, (draft) => {
                draft.currentQuickFilterId = id;
                return draft;
            });
        });
    },
}));
